import { useQuery } from "@tanstack/react-query"
import {
  BookCheck,
  BookOpen,
  BookX,
  Briefcase,
  Building2,
  CalendarX,
  CircleCheck,
  FilePen,
  FileQuestion,
  Flag,
  GitBranch,
  ListChecks,
  Network,
  Shuffle,
  Target,
  UserRoundX,
  Users,
  Workflow,
} from "lucide-react"

import { api } from "@/api/cliente"
import {
  type CargoMin,
  type DepartamentoMin,
  type DocumentoSgiMin,
  kpiBiblioteca,
  kpiCargos,
  kpiDepartamentos,
  kpiEstrategia,
  kpiOrganigrama,
  kpiProcesos,
  type NodoMin,
  type PilarMin,
  type ProcesoMin,
} from "@/componentes/kpi/calculos"
import { FilaKpi } from "@/componentes/kpi/FilaKpi"
import { TarjetaKpi } from "@/componentes/ui/TarjetaKpi"
import { comoFecha } from "@/lib/fechas"

/**
 * Los indicadores de las pestañas de Planificación estratégica. Cada fila pide sus datos
 * con la MISMA clave que su pestaña (caché compartida) y cuenta en el navegador: son
 * catálogos enteros, sin paginar.
 *
 * ⚠ **Lo que se imprime no lleva indicadores**: el organigrama y el manual salen en papel,
 * y la fila va con `no-print`.
 */

const porcentaje = (parte: number, todo: number) =>
  todo === 0 ? "—" : `${Math.round((parte / todo) * 100)} %`

export function KpiEstrategia() {
  const q = useQuery({
    queryKey: ["estrategia"],
    queryFn: () => api.get<{ avance: number; pilares: PilarMin[] }>("/api/v1/estrategia"),
  })
  const k = q.data ? kpiEstrategia(q.data.pilares) : null
  return (
    <FilaKpi cargando={q.isPending} error={q.isError} className="mb-4">
      {k && (
        <>
          <TarjetaKpi
            titulo="Objetivos"
            detalle="Del despliegue estratégico"
            Icono={Target}
            color="var(--primary)"
            cantidad={k.objetivos}
            pie={`${k.cumplidos} cumplidos al 100 %`}
          />
          <TarjetaKpi
            titulo="Vencidos"
            detalle="Pasó su fecha sin completarse"
            Icono={CalendarX}
            color="var(--destructive)"
            cantidad={k.vencidos}
          />
          <TarjetaKpi
            titulo="Etapas completadas"
            detalle="Lo único que mueve el avance"
            Icono={ListChecks}
            color="var(--success)"
            cantidad={`${k.completadas} / ${k.etapas}`}
            pie={porcentaje(k.completadas, k.etapas)}
          />
          <TarjetaKpi
            titulo="Pilares sin objetivos"
            detalle="No cuentan para el avance"
            Icono={Flag}
            color="var(--warning)"
            cantidad={k.pilaresVacios}
            pie={`De ${k.pilares} pilares`}
          />
        </>
      )}
    </FilaKpi>
  )
}

export function KpiOrganigrama({ mapaId }: { mapaId: number | undefined }) {
  const q = useQuery({
    queryKey: ["organigrama", "nodos", mapaId],
    queryFn: () => api.get<NodoMin[]>(`/api/v1/organigramas/${mapaId}/nodos`),
    enabled: mapaId !== undefined,
  })
  if (mapaId === undefined) return null
  const k = q.data ? kpiOrganigrama(q.data) : null
  return (
    <FilaKpi cargando={q.isPending} error={q.isError} className="no-print mb-4">
      {k && (
        <>
          <TarjetaKpi
            titulo="Posiciones"
            detalle="En este organigrama"
            Icono={Network}
            color="var(--primary)"
            cantidad={k.posiciones}
          />
          <TarjetaKpi
            titulo="Cubiertas"
            detalle="Con una persona asignada"
            Icono={CircleCheck}
            color="var(--success)"
            cantidad={k.cubiertas}
            pie={porcentaje(k.cubiertas, k.posiciones)}
          />
          <TarjetaKpi
            titulo="Vacantes"
            detalle="Posiciones sin persona"
            Icono={UserRoundX}
            color="var(--warning)"
            cantidad={k.vacantes}
          />
          <TarjetaKpi
            titulo="Personas"
            detalle="Distintas en el organigrama"
            Icono={Users}
            color="var(--info)"
            cantidad={k.personas}
            pie="Una persona en dos cargos cuenta una vez"
          />
        </>
      )}
    </FilaKpi>
  )
}

export function KpiDepartamentos() {
  const q = useQuery({
    queryKey: ["departamentos"],
    queryFn: () => api.get<DepartamentoMin[]>("/api/v1/departamentos"),
  })
  const k = q.data ? kpiDepartamentos(q.data) : null
  return (
    <FilaKpi cargando={q.isPending} error={q.isError} className="mb-4">
      {k && (
        <>
          <TarjetaKpi
            titulo="Áreas activas"
            detalle="Departamentos de la estructura"
            Icono={Building2}
            color="var(--primary)"
            cantidad={k.activos}
            pie={`${k.cargos} cargos en ellas`}
          />
          <TarjetaKpi
            titulo="Sin cargo responsable"
            detalle="Nadie responde por el área"
            Icono={FileQuestion}
            color="var(--destructive)"
            cantidad={k.sinResponsable}
          />
          <TarjetaKpi
            titulo="Responsable sin ocupar"
            detalle="El cargo existe; nadie lo ocupa solo"
            Icono={UserRoundX}
            color="var(--warning)"
            cantidad={k.responsableVacante}
          />
          <TarjetaKpi
            titulo="Sin cargos"
            detalle="Áreas sin ningún puesto"
            Icono={Briefcase}
            color="var(--fg-muted)"
            cantidad={k.sinCargos}
          />
        </>
      )}
    </FilaKpi>
  )
}

export function KpiCargos() {
  const q = useQuery({
    queryKey: ["cargos"],
    queryFn: () => api.get<CargoMin[]>("/api/v1/cargos"),
  })
  const k = q.data ? kpiCargos(q.data) : null
  return (
    <FilaKpi cargando={q.isPending} error={q.isError} className="mb-4">
      {k && (
        <>
          <TarjetaKpi
            titulo="Cargos activos"
            detalle="Puestos de la agencia"
            Icono={Briefcase}
            color="var(--primary)"
            cantidad={k.activos}
            pie={`${k.ocupados} ocupados`}
          />
          <TarjetaKpi
            titulo="Sin ocupante"
            detalle="Nadie tiene ese cargo"
            Icono={UserRoundX}
            color="var(--warning)"
            cantidad={k.sinOcupante}
          />
          <TarjetaKpi
            titulo="Sin funciones"
            detalle="El descriptivo está vacío"
            Icono={FilePen}
            color="var(--info)"
            cantidad={k.sinFunciones}
          />
          <TarjetaKpi
            titulo="Sin área"
            detalle="No cuelgan de un departamento"
            Icono={Building2}
            color="var(--fg-muted)"
            cantidad={k.sinArea}
          />
        </>
      )}
    </FilaKpi>
  )
}

export function KpiProcesos() {
  const q = useQuery({
    queryKey: ["procesos"],
    queryFn: () => api.get<ProcesoMin[]>("/api/v1/procesos"),
  })
  const k = q.data ? kpiProcesos(q.data) : null
  return (
    <FilaKpi cargando={q.isPending} error={q.isError} className="mb-4">
      {k && (
        <>
          <TarjetaKpi
            titulo="Procesos activos"
            detalle="Cómo trabaja la agencia"
            Icono={Workflow}
            color="var(--primary)"
            cantidad={k.activos}
            pie={`${k.transversales} transversales`}
          />
          <TarjetaKpi
            titulo="Sin responsable"
            detalle="Ningún cargo responde"
            Icono={UserRoundX}
            color="var(--destructive)"
            cantidad={k.sinResponsable}
          />
          <TarjetaKpi
            titulo="Sin procedimiento"
            detalle="No enlazan un PRO de la biblioteca"
            Icono={GitBranch}
            color="var(--warning)"
            cantidad={k.sinDocumento}
          />
          <TarjetaKpi
            titulo="Sin pasos"
            detalle="Declarados pero sin describir"
            Icono={Shuffle}
            color="var(--fg-muted)"
            cantidad={k.sinPasos}
          />
        </>
      )}
    </FilaKpi>
  )
}

export function KpiBiblioteca() {
  const q = useQuery({
    queryKey: ["biblioteca", "documentos"],
    queryFn: () => api.get<DocumentoSgiMin[]>("/api/v1/biblioteca/documentos"),
  })
  const k = q.data ? kpiBiblioteca(q.data) : null
  return (
    <FilaKpi cargando={q.isPending} error={q.isError} className="mb-4">
      {k && (
        <>
          <TarjetaKpi
            titulo="Vigentes"
            detalle="Documentos del SGI aprobados"
            Icono={BookCheck}
            color="var(--success)"
            cantidad={k.vigentes}
            pie={`De ${k.total} documentos`}
          />
          <TarjetaKpi
            titulo="Borradores"
            detalle="En redacción"
            Icono={FilePen}
            color="var(--info)"
            cantidad={k.borradores}
          />
          <TarjetaKpi
            titulo="Sin versión publicada"
            detalle="Nadie ha aprobado su texto"
            Icono={BookOpen}
            color="var(--warning)"
            cantidad={k.sinPublicar}
          />
          <TarjetaKpi
            titulo="Obsoletos"
            detalle="Se conservan, no rigen"
            Icono={BookX}
            color="var(--fg-muted)"
            cantidad={k.obsoletos}
          />
        </>
      )}
    </FilaKpi>
  )
}

type Manual = { versiones: { id: number; version: number; creado_en: string }[] }

export function KpiManual() {
  const q = useQuery({
    queryKey: ["manual"],
    queryFn: () => api.get<Manual>("/api/v1/manual"),
  })
  const versiones = q.data?.versiones ?? []
  const ultima = [...versiones].sort((a, b) => b.creado_en.localeCompare(a.creado_en))[0]
  return (
    <FilaKpi cargando={q.isPending} error={q.isError} className="no-print mb-4">
      {q.data && (
        <>
          <TarjetaKpi
            titulo="Ediciones definitivas"
            detalle="Versiones congeladas del manual"
            Icono={BookCheck}
            color="var(--primary)"
            cantidad={versiones.length}
          />
          <TarjetaKpi
            titulo="Última edición"
            detalle="Lo de después sólo está en el borrador"
            Icono={CalendarX}
            color="var(--info)"
            cantidad={ultima ? `v${ultima.version}` : "—"}
            pie={ultima ? comoFecha(ultima.creado_en) : "Ninguna congelada todavía"}
          />
        </>
      )}
    </FilaKpi>
  )
}
